import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  Typography,
} from "@material-ui/core";
import { Field, useFormikContext } from "formik";
import { RadioGroup, TextField } from "formik-material-ui";
import { KeyboardDatePicker } from "formik-material-ui-pickers";
import React from "react";
import { BirthSexOptions } from "../../../Base/AdoptionConstants";
import FormikRadioField from "../../../Base/Components/FormikRadioField";
import FormikTextField from "../../../Base/Components/FormikTextField";
import NameFields from "../../../Base/Components/NameFields";
import YesNoRadioField from "../../../Base/Components/YesNoRadioField";
import { getCities } from "../../../Base/Services/CityService";
import { getHospitals } from "../../../Base/Services/HospitalService";

export default function AdopteeInfoForm() {
  const formik = useFormikContext();
  const [cities, setCities] = React.useState([]);
  const [hospitals, setHospitals] = React.useState([]);
  React.useEffect(() => {
    if (formik.values.knowExactBirthDate === "yes") {
      formik.setValues({ ...formik.values, birthDateRangeStart: null, birthDateRangeEnd: null });
    } else if (formik.values.knowExactBirthDate === "no") {
      formik.setValues({ ...formik.values, birthDate: null });
    } else {
      formik.setValues({ ...formik.values, birthDate: null, birthDateRangeStart: null, birthDateRangeEnd: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.knowExactBirthDate]);

  React.useEffect(() => {
    getCities().then((citiesResponse) => setCities(["Unknown / Other", ...citiesResponse]));
    getHospitals().then((hospitalsResponse) => setHospitals(["Unknown / Other", ...hospitalsResponse]));
  }, []);

  return (
    <div role="main">
      <Typography variant="h3" className="h3-header">
        Adoptee Sibling(s)
      </Typography>
      <YesNoRadioField
        name="noAdoptedMinorSiblingsInHousehold"
        label="Does the adult adoptee have a sibling of the same birth parent who is under the age of 18 years, and who was raised in the same family setting as the adult adoptee?"
        noLabel="No / Unknown"
      />
      <Typography variant="h3" className="h3-header">
        Adoptee's Current Name
      </Typography>
      <NameFields namePrefix="adopteeCurrent" />
      <Typography variant="h3" className="h3-header">
        Adoptee's Name at Birth
      </Typography>
      <NameFields namePrefix="adopteeBirth" />
      <Typography variant="h3" className="h3-header">
        Adoptee's Adoptive Name
      </Typography>
      <NameFields namePrefix="adopteeAdoptive" />
      <Typography variant="h3" className="h3-header">
        Adoptee's Birth Date
      </Typography>
      <FormControl
        component="fieldset"
        style={{ width: "100%" }}
        name="knowExactBirthDate"
        error={Boolean(formik.touched.knowExactBirthDate && formik.errors.knowExactBirthDate)}
      >
        <FormLabel component="legend">Please choose one:</FormLabel>
        <Field name="knowExactBirthDate" style={{ marginTop: 12 }} component={RadioGroup}>
          <Accordion expanded={formik.values.knowExactBirthDate === "yes"} style={{ width: "100%" }}>
            <AccordionSummary
              expandIcon={null}
              arial-label="Select and Expand"
              aria-controls="know-birth-date-selection"
              id="know-birth-date-selection"
              onClick={() => formik.setFieldValue("knowExactBirthDate", "yes")}
            >
              <FormControlLabel
                value={"yes"}
                control={<Radio id="know-birth-date-selection-radio" tabIndex={-1} />}
                label="I know the adoptee's date of birth"
              />
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={KeyboardDatePicker}
                    label={"Birth Date"}
                    name="birthDate"
                    format="MM/DD/yyyy"
                    placeholder="MM/DD/YYYY"
                    style={{ marginBottom: 9 }}
                    fullWidth
                    maxDate={new Date()}
                    maxDateMessage="Invalid date of birth"
                    FormHelperTextProps={{ id: "birthDate-helper-text", "data-cy": "birthDate-helper-text" }}
                    KeyboardButtonProps={{
                      "aria-label": "Birth Date Picker",
                    }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion expanded={formik.values.knowExactBirthDate === "no"} style={{ width: "100%" }}>
            <AccordionSummary
              expandIcon={null}
              arial-label="Select and Expand"
              aria-controls="know-birth-date-selection"
              id="know-birth-date-selection-no"
              onClick={() => formik.setFieldValue("knowExactBirthDate", "no")}
            >
              <FormControlLabel
                value={"no"}
                control={<Radio id="know-birth-date-selection-no-radio" tabIndex={-1} />}
                label="I do not know the adoptee's exact date of birth"
              />
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <p style={{ marginTop: 0 }}>
                    To the best of your knowledge, please enter a date range that the adoptee was born in. (Example: if
                    the adoptee was born in November 1970, enter 11/01/1970 & 11/30/1970)
                  </p>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={KeyboardDatePicker}
                    label={"Birth Date Range Start"}
                    name="birthDateRangeStart"
                    format="MM/DD/yyyy"
                    placeholder="MM/DD/YYYY"
                    style={{ marginBottom: 9 }}
                    fullWidth
                    maxDate={new Date()}
                    maxDateMessage="Invalid date of birth"
                    FormHelperTextProps={{
                      id: "birthDateRangeStart-helper-text",
                      "data-cy": "birthDateRangeStart-helper-text",
                    }}
                    KeyboardButtonProps={{
                      "aria-label": "Birth Date Range Start Picker",
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={KeyboardDatePicker}
                    label={"Birth Date Range End"}
                    name="birthDateRangeEnd"
                    format="MM/DD/yyyy"
                    placeholder="MM/DD/YYYY"
                    style={{ marginBottom: 9 }}
                    fullWidth
                    maxDate={new Date()}
                    maxDateMessage="Invalid date of birth"
                    FormHelperTextProps={{
                      id: "birthDateRangeEnd-helper-text",
                      "data-cy": "birthDateRangeEnd-helper-text",
                    }}
                    KeyboardButtonProps={{
                      "aria-label": "Birth Date Range End Picker",
                    }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          {formik.touched.knowExactBirthDate && formik.errors.knowExactBirthDate && (
            <FormHelperText>{formik.errors.knowExactBirthDate}</FormHelperText>
          )}
        </Field>
      </FormControl>
      <Typography variant="h3" className="h3-header">
        Adoptee's Birth Location
      </Typography>
      <FormControl name="adopteeBirthLocation">
        <Grid container spacing={2} style={{ marginTop: 12 }}>
          <Grid item xs={12} sm={6}>
            {Boolean(cities.length) && (
              <Field component={TextField} select={true} name="birthCity" type="text" fullWidth label="City of Birth">
                {cities.map((city) => (
                  <MenuItem key={city} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </Field>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {Boolean(hospitals.length) && (
              <Field
                component={TextField}
                select={true}
                name="birthHospital"
                type="text"
                fullWidth
                label="Birth Hospital"
              >
                {hospitals.map((hospital) => (
                  <MenuItem key={hospital} value={hospital}>
                    {hospital}
                  </MenuItem>
                ))}
              </Field>
            )}
          </Grid>
          <Grid item xs={12}>
            <FormikTextField
              name="otherBirthLocation"
              fullWidth
              label="Other Birth Location Information"
              multiline
              rows={3}
              placeholder="Enter any information you have about the birth location"
              variant="outlined"
              maxLength={4000}
            />
          </Grid>
        </Grid>
        {Boolean(formik.errors.adopteeBirthLocation && formik.touched.adopteeBirthLocation) && (
          <FormHelperText error={true}>{formik.errors.adopteeBirthLocation}</FormHelperText>
        )}
      </FormControl>
      <Typography variant="h3" className="h3-header">
        Adoptee Birth Information
      </Typography>
      <FormikRadioField
        style={{ marginTop: 12 }}
        name="birthSex"
        label="Sex Registered at Birth"
        options={BirthSexOptions}
      />
      <FormikTextField name="adoptionAgencyAttorney" label="Adoption Agency / Attorney" maxLength={100} />
    </div>
  );
}
